import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { MessageSquare, Send, X, User, Users, ChevronDown, Search, Mail, MailOpen, Plus, Stethoscope, Eye, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const InternalMessages = ({ isOpen, onClose, currentUser }) => {
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [inbox, setInbox] = useState({ messages: [], unread: 0 });
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [view, setView] = useState('inbox'); // 'inbox', 'contacts', 'chat'
  const messagesEndRef = useRef(null);
  
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // Get user role
  const userRole = currentUser?.role?.toLowerCase?.() || currentUser?.role || 'patient';
  const isPatient = userRole === 'patient';

  useEffect(() => {
    if (isOpen) {
      loadInbox();
      loadContacts();
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedContact) {
      loadConversation(selectedContact.id);
    }
  }, [selectedContact]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadInbox = async () => {
    try {
      const res = await axios.get(`${API}/messages/inbox`, { headers });
      setInbox({
        messages: res.data?.messages || [],
        unread: res.data?.unread || 0
      });
    } catch (error) {
      console.error('Error loading inbox:', error);
      setInbox({ messages: [], unread: 0 });
    }
  };

  const loadContacts = async () => {
    try {
      const res = await axios.get(`${API}/messages/contacts`, { headers });
      setContacts(res.data.contacts || []);
    } catch (error) {
      console.error('Error loading contacts:', error);
    }
  };

  const loadConversation = async (contactId) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/messages/conversation/${contactId}`, { headers });
      setMessages(res.data.messages || []);
      setView('chat');
    } catch (error) {
      console.error('Error loading conversation:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedContact) return;

    try {
      const isGroup = selectedContact.is_group || selectedContact.id?.startsWith('group_');
      
      if (isGroup) {
        // Group channel message
        await axios.post(`${API}/messages/send`, {
          recipient_id: selectedContact.id,
          recipient_type: 'group_channel',
          content: newMessage.trim(),
          is_group_message: true,
          group_channel: selectedContact.group_key || selectedContact.id.replace('group_', '')
        }, { headers });
      } else {
        // Direct message
        await axios.post(`${API}/messages/send`, {
          recipient_id: selectedContact.id,
          recipient_type: selectedContact.role,
          content: newMessage.trim()
        }, { headers });
      }

      setNewMessage('');
      loadConversation(selectedContact.id);
      toast.success('Messaggio inviato');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Errore invio messaggio');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const filteredContacts = contacts.filter(c => {
    const name = `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase();
    return name.includes(searchQuery.toLowerCase());
  });

  const getRoleLabel = (role) => {
    const labels = {
      admin: 'Segreteria',
      doctor: 'Medico',
      ortottista: 'Ortottista',
      patient: 'Paziente',
      group_channel: 'Canale'
    };
    return labels[role] || role;
  };

  const getRoleColor = (role) => {
    const colors = {
      admin: '#EF4444',
      doctor: '#2A9FCB',
      ortottista: '#10B981',
      patient: '#6366F1',
      group_channel: '#8B5CF6'
    };
    return colors[role] || '#6B7280';
  };

  const getGroupIcon = (groupKey) => {
    const icons = {
      medico_oculista: <Stethoscope className="w-6 h-6" />,
      ortottista: <Eye className="w-6 h-6" />,
      admin: <ClipboardList className="w-6 h-6" />
    };
    return icons[groupKey] || <Users className="w-6 h-6" />;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="messages-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl h-[600px] flex flex-col shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between" style={{ background: '#2A9FCB' }}>
          <div className="flex items-center gap-3">
            <MessageSquare className="w-6 h-6 text-white" />
            <h2 className="text-lg font-bold text-white">Messaggi</h2>
            {inbox?.unread > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                {inbox.unread} nuovi
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={() => setView('contacts')}
              className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-lg transition-colors"
              title="Nuovo Messaggio"
              data-testid="new-message-btn"
            >
              <Plus className="w-5 h-5" />
            </button>
            <button onClick={onClose} className="text-white hover:bg-white/20 p-2 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => { setView('inbox'); setSelectedContact(null); }}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${view === 'inbox' ? 'text-[#2A9FCB] border-b-2 border-[#2A9FCB]' : 'text-gray-500'}`}
          >
            Ricevuti ({inbox?.unread || 0})
          </button>
          <button
            onClick={() => setView('contacts')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${view === 'contacts' ? 'text-[#2A9FCB] border-b-2 border-[#2A9FCB]' : 'text-gray-500'}`}
          >
            {isPatient ? 'Contatta' : 'Contatti'}
          </button>
          {selectedContact && (
            <button
              onClick={() => setView('chat')}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${view === 'chat' ? 'text-[#2A9FCB] border-b-2 border-[#2A9FCB]' : 'text-gray-500'}`}
            >
              Chat
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Inbox View */}
          {view === 'inbox' && (
            <div className="flex-1 overflow-y-auto p-4">
              {(!inbox.messages || inbox.messages.length === 0) ? (
                <div className="text-center py-8 text-gray-400">
                  <Mail className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Nessun messaggio ricevuto</p>
                  {isPatient && (
                    <p className="text-sm mt-2">Usa il tab "Contatta" per scrivere allo staff</p>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {inbox.messages.map((msg) => (
                    <div
                      key={msg.id}
                      onClick={() => {
                        if (msg.group_channel) {
                          setSelectedContact({ 
                            id: `group_${msg.group_channel}`, 
                            first_name: msg.sender_name,
                            last_name: '',
                            role: 'group_channel',
                            is_group: true,
                            group_key: msg.group_channel
                          });
                        } else {
                          setSelectedContact({ 
                            id: msg.sender_id, 
                            first_name: msg.sender_name?.split(' ')[0] || '', 
                            last_name: msg.sender_name?.split(' ')[1] || '', 
                            role: msg.sender_role 
                          });
                        }
                      }}
                      className={`p-4 rounded-xl cursor-pointer transition-all hover:shadow-md ${msg.read ? 'bg-gray-50' : 'bg-blue-50 border-l-4 border-blue-500'}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {msg.read ? <MailOpen className="w-4 h-4 text-gray-400" /> : <Mail className="w-4 h-4 text-blue-500" />}
                          <span className="font-medium text-gray-800">{msg.sender_name}</span>
                          {msg.is_group_message && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-600">
                              <Users className="w-3 h-3 inline mr-1" />
                              Gruppo
                            </span>
                          )}
                          {!msg.is_group_message && (
                            <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: getRoleColor(msg.sender_role) + '20', color: getRoleColor(msg.sender_role) }}>
                              {getRoleLabel(msg.sender_role)}
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-gray-400">
                          {new Date(msg.created_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-2">{msg.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Contacts View */}
          {view === 'contacts' && (
            <div className="flex-1 overflow-y-auto">
              {/* Patient: Show group channels */}
              {isPatient ? (
                <div className="p-4 space-y-3">
                  <p className="text-sm text-gray-500 mb-4">
                    Seleziona a chi vuoi scrivere. Il tuo messaggio sarà ricevuto da tutto il team.
                  </p>
                  {filteredContacts.map((contact) => (
                    <button
                      key={contact.id}
                      onClick={() => {
                        setSelectedContact(contact);
                        setView('chat');
                      }}
                      className="w-full flex items-center gap-4 p-4 rounded-xl border-2 hover:border-[#2A9FCB] transition-all text-left group"
                      style={{ borderColor: contact.color + '40' }}
                    >
                      <div 
                        className="w-14 h-14 rounded-full flex items-center justify-center text-white"
                        style={{ background: contact.color }}
                      >
                        {getGroupIcon(contact.group_key)}
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-800 text-lg">{contact.first_name}</p>
                        <p className="text-sm text-gray-500">{contact.description}</p>
                      </div>
                      <ChevronDown className="w-5 h-5 text-gray-400 -rotate-90 group-hover:text-[#2A9FCB]" />
                    </button>
                  ))}
                </div>
              ) : (
                /* Staff: Show individual contacts */
                <>
                  <div className="p-3 border-b sticky top-0 bg-white">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Cerca contatto..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm"
                      />
                    </div>
                  </div>
                  
                  <div className="p-2">
                    {filteredContacts.length === 0 ? (
                      <p className="text-center py-8 text-gray-400">Nessun contatto trovato</p>
                    ) : (
                      <div className="space-y-1">
                        {filteredContacts.map((contact) => (
                          <button
                            key={contact.id}
                            onClick={() => {
                              setSelectedContact(contact);
                              setView('chat');
                            }}
                            className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors text-left"
                          >
                            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: getRoleColor(contact.role) + '20' }}>
                              <User className="w-5 h-5" style={{ color: getRoleColor(contact.role) }} />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-gray-800">{contact.first_name} {contact.last_name}</p>
                              <div className="flex items-center gap-2">
                                <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: getRoleColor(contact.role) + '20', color: getRoleColor(contact.role) }}>
                                  {getRoleLabel(contact.role)}
                                </span>
                                {contact.specialization && (
                                  <span className="text-xs text-gray-500">{contact.specialization}</span>
                                )}
                              </div>
                            </div>
                            <ChevronDown className="w-4 h-4 text-gray-400 -rotate-90" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Chat View */}
          {view === 'chat' && selectedContact && (
            <>
              {/* Chat Header */}
              <div className="p-3 border-b bg-gray-50 flex items-center gap-3">
                {selectedContact.is_group ? (
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white"
                    style={{ background: selectedContact.color || '#2A9FCB' }}
                  >
                    {getGroupIcon(selectedContact.group_key)}
                  </div>
                ) : (
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: getRoleColor(selectedContact.role) + '20' }}>
                    <User className="w-5 h-5" style={{ color: getRoleColor(selectedContact.role) }} />
                  </div>
                )}
                <div>
                  <p className="font-medium text-gray-800">
                    {selectedContact.first_name} {selectedContact.last_name}
                  </p>
                  {selectedContact.is_group ? (
                    <span className="text-xs text-purple-600">
                      <Users className="w-3 h-3 inline mr-1" />
                      Chat di gruppo
                    </span>
                  ) : (
                    <span className="text-xs" style={{ color: getRoleColor(selectedContact.role) }}>
                      {getRoleLabel(selectedContact.role)}
                    </span>
                  )}
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ background: '#F9FAFB' }}>
                {loading ? (
                  <div className="text-center py-8 text-gray-400">Caricamento...</div>
                ) : messages.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Inizia una conversazione</p>
                    {selectedContact.is_group && (
                      <p className="text-sm mt-2">
                        Il tuo messaggio sarà ricevuto da tutto il team {selectedContact.first_name}
                      </p>
                    )}
                  </div>
                ) : (
                  messages.map((msg) => {
                    const isOwnMessage = msg.sender_id === currentUser?.id;
                    return (
                      <div key={msg.id} className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[70%] p-3 rounded-2xl ${isOwnMessage ? 'bg-[#2A9FCB] text-white rounded-br-none' : 'bg-white shadow-sm rounded-bl-none'}`}>
                          {/* Show sender name for group messages */}
                          {!isOwnMessage && msg.is_staff_reply && (
                            <p className="text-xs font-medium mb-1 text-[#2A9FCB]">
                              {msg.sender_name}
                            </p>
                          )}
                          <p className="text-sm">{msg.content}</p>
                          <p className={`text-xs mt-1 ${isOwnMessage ? 'text-white/70' : 'text-gray-400'}`}>
                            {new Date(msg.created_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <div className="p-3 border-t bg-white">
                <div className="flex gap-2">
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={selectedContact.is_group 
                      ? `Scrivi al team ${selectedContact.first_name}...` 
                      : "Scrivi un messaggio..."}
                    className="flex-1 p-3 border rounded-xl resize-none text-sm focus:ring-2 focus:ring-[#2A9FCB]/30 focus:border-[#2A9FCB]"
                    rows={2}
                  />
                  <button
                    onClick={sendMessage}
                    disabled={!newMessage.trim()}
                    className="px-4 py-2 rounded-xl text-white transition-colors disabled:opacity-50"
                    style={{ background: '#2A9FCB' }}
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default InternalMessages;
